import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { BrowserProvider, JsonRpcSigner } from "ethers";
import { ALLOWED_CHAIN_IDS, CONTRACT_OWNER_ADDRESS, getNetworkName, POLYGON_CHAIN_ID } from "../config/web3Config";
import { CHAINS } from "../utils/chains";

interface WalletContextType {
  account: string;
  chainId: number;
  provider: BrowserProvider | null;
  signer: JsonRpcSigner | null;
  isConnected: boolean;
  isCorrectNetwork: boolean;
  isOwner: boolean;
  walletReady: boolean;
  connectWallet: () => Promise<void>;
  disconnectWallet: () => void;
  switchNetwork: (targetChainId: number) => Promise<void>;
}

const WalletContext = createContext<WalletContextType>({
  account: "", chainId: 0, provider: null, signer: null,
  isConnected: false, isCorrectNetwork: false, isOwner: false, walletReady: false,
  connectWallet: async () => {}, disconnectWallet: () => {},
  switchNetwork: async () => {},
});

export const useWallet = () => useContext(WalletContext);

export function WalletProvider({ children }: { children: ReactNode }) {
  const [account,     setAccount]     = useState("");
  const [chainId,     setChainId]     = useState(0);
  const [provider,    setProvider]    = useState<BrowserProvider | null>(null);
  const [signer,      setSigner]      = useState<JsonRpcSigner | null>(null);
  const [walletReady, setWalletReady] = useState(false);

  const isConnected      = !!account;
  const isCorrectNetwork = ALLOWED_CHAIN_IDS.includes(chainId);
  const isOwner          = account.toLowerCase() === CONTRACT_OWNER_ADDRESS.toLowerCase();

  const buildProvider = () =>
    window.ethereum ? new BrowserProvider(window.ethereum as any, "any") : null;

  const connectWallet = async () => {
    if (!window.ethereum) throw new Error("MetaMask is not installed.");
    const prov = buildProvider()!;
    await (window.ethereum as any).request({ method: "eth_requestAccounts" });
    const net  = await prov.getNetwork();
    const sign = await prov.getSigner();
    const addr = await sign.getAddress();
    setProvider(prov); setSigner(sign);
    setAccount(addr); setChainId(Number(net.chainId));
    localStorage.setItem("walletConnected", "true");
  };

  const disconnectWallet = () => {
    localStorage.removeItem("walletConnected");
    setAccount(""); setChainId(0); setProvider(null); setSigner(null);
  };

  const switchNetwork = async (targetChainId: number) => {
    if (!window.ethereum) throw new Error("No wallet");
    const hex = "0x" + targetChainId.toString(16);
    try {
      await (window.ethereum as any).request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: hex }],
      });
    } catch (err: any) {
      if (err.code === 4902) {
        const key = Object.keys(CHAINS).find(k => CHAINS[k].chainId === targetChainId);
        const chain = key ? CHAINS[key] : null;
        if (chain) {
          await (window.ethereum as any).request({
            method: "wallet_addEthereumChain",
            params: [{
              chainId: hex, chainName: chain.name,
              nativeCurrency: { name: chain.currency, symbol: chain.currency, decimals: 18 },
              rpcUrls: chain.rpcUrls,
              blockExplorerUrls: [chain.blockExplorer],
            }],
          });
        }
      } else throw err;
    }
  };

  useEffect(() => {
    const init = async () => {
      if (localStorage.getItem("walletConnected") === "true") {
        try { await connectWallet(); } catch { /* user not connected */ }
      }
      setWalletReady(true);
    };
    init();

    if (!window.ethereum) return;
    const eth = window.ethereum as any;
    const onAccounts = (accounts: string[]) => {
      if (accounts.length > 0) { setAccount(accounts[0]); }
      else disconnectWallet();
    };
    const onChain = async (hex: string) => {
      setChainId(parseInt(hex, 16));
      const prov = buildProvider();
      if (prov) { setProvider(prov); setSigner(await prov.getSigner()); }
    };
    eth.on("accountsChanged", onAccounts);
    eth.on("chainChanged", onChain);
    return () => { eth.removeListener("accountsChanged", onAccounts); eth.removeListener("chainChanged", onChain); };
  }, []);

  return (
    <WalletContext.Provider value={{
      account, chainId, provider, signer,
      isConnected, isCorrectNetwork, isOwner, walletReady,
      connectWallet, disconnectWallet, switchNetwork,
    }}>
      {children}
    </WalletContext.Provider>
  );
}
